/**
 * FinalizeInvitesModal Component
 * Modal for finalizing sessions and sending calendar invites
 * MED-173: Session finalization and invite sending feature
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  Text,
  Paper,
  Divider,
  List,
  ThemeIcon,
  Badge,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { showInviteSendOutcome } from '../../../utils/inviteSendNotifications';
import { platformDefaultLink } from '../../../utils/meetingLink';
import { Video, Check, Calendar, Bell, Users, AlertTriangle, MapPin } from 'react-feather';
import { sessionsApi } from '../../../services/api/sessions';
import type { CohortWithDetails, GroupFinalizationInfo, MeetingPlatform } from '../../../types';
import { MEETING_PLATFORM_OPTIONS } from '../../../config/cohortConfig';

/** Resolve a coach's meeting link for a given platform */
function getCoachLinkForPlatform(
  group: GroupFinalizationInfo,
  platform: MeetingPlatform | null,
): string | null {
  if (!platform) return null;
  switch (platform) {
    case 'zoom': return group.coach_zoom_link;
    case 'teams': return group.coach_teams_link;
    case 'google_meet': return group.coach_google_meet_link;
    case 'in_person': return null;
    default: return null;
  }
}

interface FinalizeInvitesModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  /** Specific group IDs to finalize. If empty/undefined, finalizes all unfinalized groups */
  groupIds?: string[];
  /** Callback after successful finalization */
  onFinalize?: () => void;
  /** Mode: 'finalize' for first time, 'resend' for already finalized */
  mode?: 'finalize' | 'resend';
}

export function FinalizeInvitesModal({
  opened,
  onClose,
  cohort,
  groupIds,
  onFinalize,
  mode = 'finalize',
}: FinalizeInvitesModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  // MED-1360: a cohort default from a different platform is not prefilled.
  const cohortDefaultLink = platformDefaultLink(
    cohort.default_meeting_link,
    cohort.default_meeting_platform,
  );
  const [videoLink, setVideoLink] = useState(cohortDefaultLink);
  // MED-780: Free-text physical location field. Optional; pre-fills from any
  // saved location on the target groups' sessions.
  const [location, setLocation] = useState('');
  const [groups, setGroups] = useState<GroupFinalizationInfo[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [futureInviteCount, setFutureInviteCount] = useState(0);
  const [coachLinkWarning, setCoachLinkWarning] = useState<string | null>(null);

  // Load finalization status when modal opens
  useEffect(() => {
    if (!opened) return;

    const loadStatus = async () => {
      setLoading(true);
      setCoachLinkWarning(null);
      try {
        const status = await sessionsApi.getFinalizationStatus(cohort.id);

        // Determine which groups to show based on mode and groupIds
        let targetGroups: GroupFinalizationInfo[];
        if (mode === 'resend') {
          // For resend, show finalized groups
          targetGroups = groupIds?.length
            ? status.finalized_groups.filter((g) => groupIds.includes(g.id))
            : status.finalized_groups;
        } else {
          // For finalize, show unfinalized groups
          targetGroups = groupIds?.length
            ? status.unfinalized_groups.filter((g) => groupIds.includes(g.id))
            : status.unfinalized_groups;
        }

        setGroups(targetGroups);
        setTotalSessions(targetGroups.reduce((sum, g) => sum + g.session_count, 0));
        setTotalParticipants(targetGroups.reduce((sum, g) => sum + g.participant_count, 0));
        setFutureInviteCount(targetGroups.reduce((sum, g) => sum + (g.future_session_count ?? g.session_count), 0));

        // MED-780 / AC13: pre-fill the Location field from whichever target
        // group already has a saved location. If multiple groups have
        // different saved values, take the first non-empty one — the CM can
        // edit before submitting.
        const firstSavedLocation = targetGroups
          .map(g => g.saved_location)
          .find(value => typeof value === 'string' && value.trim().length > 0);
        setLocation(firstSavedLocation ?? '');

        // Auto-populate video link from coach profile based on cohort's default meeting platform
        const platform = cohort.default_meeting_platform as MeetingPlatform | null;
        if (platform && platform !== 'in_person' && targetGroups.length > 0) {
          const firstGroup = targetGroups[0];
          const coachLink = getCoachLinkForPlatform(firstGroup, platform);
          if (coachLink) {
            setVideoLink(coachLink);
          } else {
            // Fall back to the cohort default, when it is on the same platform
            setVideoLink(cohortDefaultLink);
            const platformLabel = MEETING_PLATFORM_OPTIONS.find(o => o.value === platform)?.label || platform;
            if (firstGroup.coach_name) {
              setCoachLinkWarning(
                `${firstGroup.coach_name} does not have a ${platformLabel} link configured. ${
                  cohortDefaultLink ? 'Using cohort default.' : 'Enter a link.'
                }`,
              );
            }
          }
        } else if (platform === 'in_person') {
          setVideoLink('');
        } else {
          setVideoLink(cohortDefaultLink);
        }
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to load finalization status',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [opened, cohort.id, cohortDefaultLink, cohort.default_meeting_platform, groupIds, mode]);

  const handleSubmit = async () => {
    // MED-780: video link is no longer required. URL format is still
    // validated when a value is entered so we don't pass garbage to Google.
    if (videoLink.trim()) {
      try {
        new URL(videoLink.trim());
      } catch {
        notifications.show({
          title: 'Validation Error',
          message: 'Please enter a valid URL for the video conferencing link',
          color: 'red',
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (mode === 'resend') {
        // Resend invites for already finalized groups
        const groupIdsToResend = groups.map((g) => g.id);
        const result = await sessionsApi.resendInvites(cohort.id, groupIdsToResend);

        // MED-1300 (AC1-AC5, AC9): report what actually happened — dropped
        // recipients and email-fallback delivery were previously invisible.
        showInviteSendOutcome({
          kind: 'session',
          mode: 'resend',
          sessionsProcessed:
            result.sessions_processed ?? result.sessions_updated,
          skippedParticipants: result.skipped_participants,
          emailFallbackSessions: result.email_fallback_sessions,
          errorCount: result.errors?.length,
        });
      } else {
        // Finalize and send invites
        const result = await sessionsApi.finalizeAndSendInvites(cohort.id, {
          group_ids: groupIds,
          video_link: videoLink.trim(),
          location: location.trim(),
        });

        // MED-1300 (AC1-AC5): the blanket "Schedule Finalized" success is
        // replaced by the actual outcome. A clean send stays a clean success.
        showInviteSendOutcome({
          kind: 'session',
          mode: 'finalize',
          sessionsProcessed:
            result.sessions_processed ?? result.finalized_count,
          peopleCount: totalParticipants,
          skippedParticipants: result.skipped_participants,
          emailFallbackSessions: result.email_fallback_sessions,
          errorCount: result.errors?.length,
        });
      }

      onFinalize?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to finalize sessions',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isFinalize = mode === 'finalize';
  const allPast = isFinalize && futureInviteCount === 0 && totalSessions > 0;
  const modalTitle = allPast ? 'Finalize & Mark as Sent' : isFinalize ? 'Finalize & Send Invites' : 'Resend Invites';
  const submitButtonText = allPast ? 'Finalize & Mark as Sent' : isFinalize ? 'Send Invites' : 'Resend Invites';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">
            {modalTitle}
          </Text>
          <Text size="sm" c="dimmed">
            {groups.length === 1
              ? groups[0].name
              : `${groups.length} groups`}
          </Text>
        </Stack>
      }
      size="lg"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : groups.length === 0 ? (
        <Stack align="center" py="xl">
          <Text c="dimmed">
            {isFinalize
              ? 'No groups available to finalize'
              : 'No finalized groups to resend invites'}
          </Text>
        </Stack>
      ) : (
        <Stack gap="md">
          {/* Groups Summary */}
          <Paper withBorder p="md">
            <Stack gap="xs">
              {groups.map((group) => (
                <Group key={group.id} justify="space-between">
                  <Group gap="xs">
                    <Users size={14} />
                    <Text fw={500}>{group.name}</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge variant="light" color="gray" size="sm">
                      {group.participant_count} participant{group.participant_count !== 1 ? 's' : ''}
                    </Badge>
                    <Badge variant="light" color="blue" size="sm">
                      {group.session_count} session{group.session_count !== 1 ? 's' : ''}
                    </Badge>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Paper>

          {/* What happens section - only show for finalize mode */}
          {isFinalize && (
            <>
              <Divider />
              <Stack gap="xs">
                <Text fw={500} size="sm">
                  What happens when you send invites:
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon color="green" size={20} radius="xl">
                      <Check size={12} />
                    </ThemeIcon>
                  }
                >
                  <List.Item
                    icon={
                      <ThemeIcon color="blue" size={20} radius="xl">
                        <Calendar size={12} />
                      </ThemeIcon>
                    }
                  >
                    Google Calendar invites sent to each participant in the group and to the Medley Team calendar
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon color="blue" size={20} radius="xl">
                        <Video size={12} />
                      </ThemeIcon>
                    }
                  >
                    Video conferencing link included in calendar event
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon color="green" size={20} radius="xl">
                        <Check size={12} />
                      </ThemeIcon>
                    }
                  >
                    RSVP tracking enabled - responses will appear in Sessions tab
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon color="orange" size={20} radius="xl">
                        <Bell size={12} />
                      </ThemeIcon>
                    }
                  >
                    Reminder emails scheduled 24 hours before each session
                  </List.Item>
                </List>
              </Stack>
            </>
          )}

          <Divider />

          {/* Coach link fallback warning */}
          {coachLinkWarning && (
            <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
              {coachLinkWarning}
            </Alert>
          )}

          {/* MED-780: Video link is now always optional and the field is
              visible regardless of default_meeting_platform. The in-person
              hint stays for context but no longer hides the input. */}
          {cohort.default_meeting_platform === 'in_person' && (
            <Alert variant="light" color="gray">
              <Text size="sm">
                This cohort uses in-person meetings. The video link is optional —
                use the Location field below for the room/address.
              </Text>
            </Alert>
          )}
          <TextInput
            label="Video Conferencing Link"
            placeholder="https://zoom.us/j/..."
            description="Optional — leave blank if there's no meeting link."
            leftSection={<Video size={16} />}
            value={videoLink}
            onChange={(e) => setVideoLink(e.currentTarget.value)}
          />
          <TextInput
            label="Location"
            placeholder="e.g., Room 204, 123 Main St"
            description="Optional — appears as the location on calendar invites. Enter a room name, address, or both (e.g., 'Room 204, 123 Main St')."
            leftSection={<MapPin size={16} />}
            value={location}
            onChange={(e) => setLocation(e.currentTarget.value)}
            maxLength={1000}
          />

          {/* Invite Description Preview */}
          <Paper withBorder p="md" bg="gray.0">
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Invite Details
              </Text>
              <Text size="sm" c="dimmed">
                Participants will receive a Google Calendar invite with the session title, date, time,
                and video conferencing link.
              </Text>
            </Stack>
          </Paper>

          <Divider />

          {/* Footer with count */}
          <Text size="sm" c="dimmed" ta="center">
            {futureInviteCount > 0
              ? `${futureInviteCount} calendar invite${futureInviteCount !== 1 ? 's' : ''} will be sent to all ${totalParticipants} participant${totalParticipants !== 1 ? 's' : ''}${futureInviteCount < totalSessions ? ` (${totalSessions - futureInviteCount} past session${totalSessions - futureInviteCount !== 1 ? 's' : ''} will be finalized without invites)` : ''}`
              : `${totalSessions} session${totalSessions !== 1 ? 's' : ''} will be finalized and marked as sent (all dates are in the past)`}
          </Text>

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => setConfirmOpened(true)} loading={submitting}>
              {submitButtonText}
            </Button>
          </Group>
        </Stack>
      )}

      {/* MED-646: Send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Invite Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              {allPast ? (
                <>
                  You are about to finalize{' '}
                  <Text component="span" fw={700}>
                    {totalSessions} session{totalSessions !== 1 ? 's' : ''}
                  </Text>{' '}
                  and mark them as sent. No calendar invites will be sent since all dates are in the past.
                </>
              ) : (
                <>
                  You are about to send{' '}
                  <Text component="span" fw={700}>
                    {futureInviteCount} calendar invite{futureInviteCount !== 1 ? 's' : ''}
                  </Text>{' '}
                  to{' '}
                  <Text component="span" fw={700}>
                    {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''}
                  </Text>
                  . This action cannot be undone.
                </>
              )}
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              leftSection={<Calendar size={14} />}
              onClick={() => {
                setConfirmOpened(false);
                handleSubmit();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default FinalizeInvitesModal;
